/**
 * Zugriff auf die eigene Schnittstelle. Ein Weg fuer alle Aufrufe, damit
 * Fehlerbehandlung und Abmeldung bei abgelaufener Sitzung an einer Stelle
 * stehen und nicht in jeder Ansicht neu geschrieben werden.
 */

export class ApiFehler extends Error {
  constructor(
    readonly status: number,
    override readonly message: string,
    readonly feld?: string,
  ) {
    super(message)
    this.name = 'ApiFehler'
  }
}

type Verfahren = 'GET' | 'POST' | 'PATCH' | 'DELETE'

async function anfragen<T>(verfahren: Verfahren, pfad: string, koerper?: unknown): Promise<T> {
  const optionen: RequestInit = { method: verfahren, credentials: 'same-origin' }
  if (koerper instanceof FormData) {
    optionen.body = koerper
  } else if (koerper !== undefined) {
    optionen.headers = { 'content-type': 'application/json' }
    optionen.body = JSON.stringify(koerper)
  }

  let antwort: Response
  try {
    antwort = await fetch(pfad, optionen)
  } catch {
    throw new ApiFehler(0, 'Der Server ist nicht erreichbar.')
  }

  if (antwort.status === 204) return undefined as T

  const inhalt = antwort.headers.get('content-type') ?? ''
  const daten: unknown = inhalt.includes('application/json') ? await antwort.json() : await antwort.text()

  if (!antwort.ok) {
    const nachricht =
      typeof daten === 'object' && daten !== null && 'fehler' in daten && typeof daten.fehler === 'string'
        ? daten.fehler
        : 'Es ist ein Fehler aufgetreten.'
    const feld =
      typeof daten === 'object' && daten !== null && 'feld' in daten && typeof daten.feld === 'string'
        ? daten.feld
        : undefined
    // Abgelaufene Sitzung: die Oberflaeche soll nicht bei jedem Klick
    // "Nicht angemeldet" sagen, sondern zur Anmeldung zurueck. Die Anmeldung
    // selbst ist ausgenommen, dort heisst 401 "falsches Passwort".
    if (antwort.status === 401 && !pfad.startsWith('/api/anmeldung')) {
      window.dispatchEvent(new Event('sitzung-abgelaufen'))
    }
    throw new ApiFehler(antwort.status, nachricht, feld)
  }

  return daten as T
}

export const api = {
  holen: <T>(pfad: string) => anfragen<T>('GET', pfad),
  senden: <T>(pfad: string, koerper?: unknown) => anfragen<T>('POST', pfad, koerper),
  aendern: <T>(pfad: string, koerper: unknown) => anfragen<T>('PATCH', pfad, koerper),
  loeschen: <T>(pfad: string) => anfragen<T>('DELETE', pfad),
}

/* ─────────────────────────────────────────── Gestalten der Schnittstelle */

export type Rolle = 'verwaltung' | 'kanzlei'
export type AuszugStand = 'pruefen' | 'bereit' | 'freigegeben' | 'doppelt' | 'exportiert'
export type DokumentStand = 'wartet' | 'wird_gelesen' | 'gelesen' | 'fehler'
export type Dokumenttyp = 'kontoauszug' | 'kreditkartenabrechnung'
export type Kontoart = 'giro' | 'kreditkarte'
export type ExportFormat = 'camt053' | 'mt940' | 'datev_extf'

/** Wie ein gelesenes Dokument sein Konto nennt: IBAN oder Karte. */
export interface Kontoangaben {
  dokumenttyp: Dokumenttyp
  kontokennung: string | null
  kartennummer_maskiert: string | null
  kartenanbieter: string | null
}

export interface Benutzer {
  id: string
  email: string
  name: string
  rolle: Rolle
}

export interface Sitzungsauskunft {
  benutzer: Benutzer
  kanzlei: string | null
  loeschfristTage: number | null
  leserEingerichtet: boolean
}

export interface AuszugKurz extends Kontoangaben {
  id: string
  dokument_id: string
  reihenfolge: number
  iban: string | null
  bank: string | null
  inhaber: string | null
  auszug_nummer: string | null
  zeitraum_von: string | null
  zeitraum_bis: string | null
  stand: AuszugStand
  mandant_id: string | null
  mandant_name: string | null
  nachgelesen: boolean
  buchungen: number
}

export interface Eingangsdokument {
  id: string
  dateiname: string
  seiten: number | null
  hat_textebene: boolean | null
  stand: DokumentStand
  fehler: string | null
  eingegangen_am: string
  gelesen_am: string | null
  auszuege: AuszugKurz[]
}

export interface Befund {
  art: string
  blockt: boolean
  freigebbar: boolean
  titel: string
  text: string
  positionen?: number[]
  bezug?: string
}

export interface Buchung {
  id: string
  position: number
  buchungsdatum: string | null
  valuta: string | null
  /** Kaufdatum auf der Kartenabrechnung. */
  belegdatum: string | null
  originalbetrag_cent: string | null
  originalwaehrung: string | null
  kurs: string | null
  betrag_cent: string
  richtung: 'CRDT' | 'DBIT'
  betrag_gedruckt: string | null
  buchungstext: string | null
  gegenpartei: string | null
  gegen_iban: string | null
  gegen_bic: string | null
  verwendungszweck: string | null
  end_to_end_id: string | null
  mandatsreferenz: string | null
  glaeubiger_id: string | null
  seite: number | null
  von_hand: boolean
  nachgelesen: boolean
}

export interface AuszugVoll extends Kontoangaben {
  id: string
  dokument_id: string
  mandant_id: string | null
  zuordnung: 'iban' | 'kontokennung' | 'hand' | null
  seiten: number[]
  iban: string | null
  bic: string | null
  bank: string | null
  inhaber: string | null
  auszug_nummer: string | null
  waehrung: string
  zeitraum_von: string | null
  zeitraum_bis: string | null
  anfangssaldo_cent: string | null
  schlusssaldo_cent: string | null
  summe_soll_gedruckt_cent: string | null
  summe_haben_gedruckt_cent: string | null
  stand: AuszugStand
  freigabe_grund: string | null
  freigegeben_am: string | null
  nachgelesen: boolean
  bezeichnung: string
}

export interface Saldo {
  summeHaben: string
  summeSoll: string
  bewegung: string
  errechnet: string | null
  gehtAuf: boolean | null
  differenz: string | null
}

export interface Auszugansicht {
  auszug: AuszugVoll
  buchungen: Buchung[]
  dokument: { id: string; dateiname: string; seiten: number | null; hat_textebene: boolean | null; eingegangen_am: string }
  mandant: { id: string; name: string; mandantennummer: number | null } | null
  kontoGehoertZu: { id: string; name: string } | null
  geschwister: { id: string; reihenfolge: number }[]
  befunde: Befund[]
  saldo: Saldo | null
}

export interface MandantZeile {
  id: string
  name: string
  mandantennummer: number | null
  konten: number
  zu_pruefen: number
  bereit: number
  letzter_export: string | null
}

export interface Bankkonto {
  id: string
  kontoart: Kontoart
  kontokennung: string
  iban: string | null
  kartenanbieter: string | null
  karten_endziffern: string | null
  exportformat: ExportFormat | null
  datev_konto: number | null
  datev_gegenkonto: number | null
  bic: string | null
  bank: string | null
  inhaber: string | null
  rz_umsaetze_ab: string | null
}

export interface ExportZeile {
  id: string
  format: ExportFormat
  version: string | null
  dateiname: string
  anzahl_auszuege: number
  anzahl_buchungen: number
  zeitraum_von: string | null
  zeitraum_bis: string | null
  erzeugt_am: string
  ibans: string[]
  mandant_id?: string
  mandant_name?: string
  mandantennummer?: number | null
  erzeugt_von?: string | null
}

export interface MandantStamm {
  id: string
  name: string
  mandantennummer: number | null
  notiz: string | null
  /** Header des DATEV-Buchungsstapels. */
  beraternummer: number | null
  wj_beginn_monat: number
  sachkontenlaenge: number
}

export interface Mandantenakte {
  mandant: MandantStamm
  konten: Bankkonto[]
  auszuege: (Omit<AuszugKurz, 'dokument_id' | 'reihenfolge' | 'inhaber' | 'mandant_id' | 'mandant_name'> & {
    dokument_id: string
    dateiname: string
    anfangssaldo_cent: string | null
    schlusssaldo_cent: string | null
    export_id: string | null
    bezeichnung: string
  })[]
  exporte: ExportZeile[]
}
